/**
 * GlobalLoaderMindMap — variante 2 du loader global : mind map qui clignote
 * autour d'un nœud central en forme de boussole.
 *
 * @remarks
 * - Même pilotage que `GlobalLoader` (`useGlobalLoaderVisible`) et même voile
 *   (`GlobalLoaderOverlay`) — seul le visuel change.
 * - Le nœud central reprend le vocabulaire du symbole Alt (anneau, arc
 *   aurore violet → ambre, point ambre) avec une aiguille qui « cherche le
 *   nord » (`compassSeek`).
 * - Style « croquis au feutre » : filtre SVG bruit + déplacement pour faire
 *   trembler les traits, chaque bulle repassée d'un second contour pâle et
 *   légèrement tourné, gribouillis d'écriture à l'intérieur. Six branches
 *   colorées autour de la boussole, sous-bulles groupées autour de leur
 *   bulle mère, disposition organique. Allumage en vague branche par branche
 *   (`mindmapBlink`, délais par `nth-of-type` dans `globals.css`).
 * - Couleurs 100 % tokens (`--color-brand`, `--color-warning`, `--color-border`,
 *   `--color-surface`) : suit le thème clair/sombre sans variante d'asset.
 */

import { GlobalLoaderOverlay } from './GlobalLoader';
import { useIsVeiled } from '@/shared/ui/loader-veil-context';

/**
 * Bulles de la mind map, dans l'ordre d'allumage (branche par branche,
 * bulle mère puis ses sous-bulles). Positions pré-calculées pour un viewBox
 * 360 × 360, boussole centrée en (180, 180) : les traits partent et arrivent
 * au bord des bulles (modèle ellipse), jamais à travers.
 */
interface Bubble {
  color: string;
  level: 1 | 2;
  shape: 'rect' | 'ellipse';
  /** Bulle : [cx, cy, rx, ry]. */
  bubble: readonly [number, number, number, number];
  /** Trait de liaison : [x1, y1, x2, y2]. */
  line: readonly [number, number, number, number];
  /** Gribouillis « écriture » (1 ou 2 lignes), en `d` de path. */
  text: readonly string[];
}

const BUBBLES: readonly Bubble[] = [
  {
    color: 'var(--color-info)',
    level: 1,
    shape: 'ellipse',
    bubble: [128, 72, 30, 24],
    line: [160, 138, 138, 93],
    text: [
      'M109 68 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0',
      'M109 76 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0',
    ],
  },
  {
    color: 'var(--color-info)',
    level: 2,
    shape: 'rect',
    bubble: [52, 30, 28, 9],
    line: [104, 59, 65, 37],
    text: [
      'M34 30 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0',
    ],
  },
  {
    color: 'var(--color-info)',
    level: 2,
    shape: 'rect',
    bubble: [50, 60, 31, 9],
    line: [100, 68, 76, 64],
    text: [
      'M31 60 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0',
    ],
  },
  {
    color: 'var(--color-info)',
    level: 2,
    shape: 'rect',
    bubble: [56, 90, 25, 9],
    line: [100, 79, 75, 85],
    text: [
      'M40 90 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0',
    ],
  },
  {
    color: 'var(--color-primary-300)',
    level: 1,
    shape: 'ellipse',
    bubble: [248, 80, 26, 26],
    line: [206, 141, 234, 101],
    text: [
      'M232 76 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0',
      'M232 84 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0',
    ],
  },
  {
    color: 'var(--color-primary-300)',
    level: 2,
    shape: 'rect',
    bubble: [300, 30, 27, 9],
    line: [266, 63, 292, 38],
    text: [
      'M283 30 q2.8 -3 5.6 0 q2.8 3 5.6 0 q2.8 -3 5.6 0 q2.8 3 5.6 0 q2.8 -3 5.6 0 q2.8 3 5.6 0',
    ],
  },
  {
    color: 'var(--color-primary-300)',
    level: 2,
    shape: 'rect',
    bubble: [312, 72, 22, 9],
    line: [273, 77, 292, 74],
    text: ['M298 72 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0'],
  },
  {
    color: 'var(--color-primary-300)',
    level: 2,
    shape: 'rect',
    bubble: [306, 114, 24, 9],
    line: [270, 93, 294, 107],
    text: [
      'M291 114 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0',
    ],
  },
  {
    color: 'var(--color-success)',
    level: 1,
    shape: 'rect',
    bubble: [312, 168, 33, 11],
    line: [227, 176, 281, 171],
    text: [
      'M291 168 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0',
    ],
  },
  {
    color: 'var(--color-error)',
    level: 1,
    shape: 'rect',
    bubble: [270, 232, 33, 11],
    line: [221, 204, 255, 223],
    text: [
      'M249 232 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0 q2.6 -3 5.2 0 q2.6 3 5.2 0',
    ],
  },
  {
    color: 'var(--color-error)',
    level: 2,
    shape: 'ellipse',
    bubble: [222, 296, 19, 19],
    line: [263, 242, 233, 282],
    text: [
      'M210 292 q3.0 -3 5.9 0 q3.0 3 5.9 0 q3.0 -3 5.9 0 q3.0 3 5.9 0',
      'M210 300 q3.0 -3 5.9 0 q3.0 3 5.9 0 q3.0 -3 5.9 0 q3.0 3 5.9 0',
    ],
  },
  {
    color: 'var(--color-error)',
    level: 2,
    shape: 'ellipse',
    bubble: [280, 300, 19, 19],
    line: [271, 242, 277, 282],
    text: ['M268 300 q3.0 -3 5.9 0 q3.0 3 5.9 0 q3.0 -3 5.9 0 q3.0 3 5.9 0'],
  },
  {
    color: 'var(--color-error)',
    level: 2,
    shape: 'ellipse',
    bubble: [334, 282, 20, 18],
    line: [282, 241, 320, 271],
    text: ['M322 282 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0'],
  },
  {
    color: 'var(--color-warning)',
    level: 1,
    shape: 'ellipse',
    bubble: [60, 178, 28, 20],
    line: [133, 179, 87, 178],
    text: [
      'M42 178 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0',
    ],
  },
  {
    color: 'var(--color-brand)',
    level: 1,
    shape: 'ellipse',
    bubble: [112, 262, 44, 26],
    line: [150, 216, 131, 239],
    text: [
      'M84 262 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0 q2.5 3 5.0 0 q2.5 -3 5.0 0',
    ],
  },
  {
    color: 'var(--color-brand)',
    level: 2,
    shape: 'ellipse',
    bubble: [58, 325, 26, 17],
    line: [93, 284, 70, 311],
    text: [
      'M42 325 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0 q2.7 -3 5.4 0 q2.7 3 5.4 0',
    ],
  },
  {
    color: 'var(--color-brand)',
    level: 2,
    shape: 'ellipse',
    bubble: [150, 330, 34, 17],
    line: [125, 286, 141, 315],
    text: [
      'M129 330 q2.7 -3 5.3 0 q2.7 3 5.3 0 q2.7 -3 5.3 0 q2.7 3 5.3 0 q2.7 -3 5.3 0 q2.7 3 5.3 0 q2.7 -3 5.3 0 q2.7 3 5.3 0',
    ],
  },
  {
    color: 'var(--color-brand)',
    level: 2,
    shape: 'ellipse',
    bubble: [60, 222, 22, 16],
    line: [86, 242, 74, 233],
    text: ['M46 222 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0 q2.8 3 5.5 0 q2.8 -3 5.5 0'],
  },
];

/**
 * Bulle « feutre » : contour principal + second contour légèrement tourné et
 * plus pâle, comme un trait repassé à la main. Le fond masque les traits.
 */
function BubbleShape({ b }: { b: Bubble }) {
  const [cx, cy, rx, ry] = b.bubble;
  const stroke = b.level === 1 ? 2.4 : 1.8;
  const common = { fill: 'var(--color-surface)', stroke: b.color } as const;
  const outline = (width: number, opacity: number, transform?: string) =>
    b.shape === 'rect' ? (
      <rect
        x={cx - rx}
        y={cy - ry}
        width={rx * 2}
        height={ry * 2}
        rx={ry * 0.8}
        strokeWidth={width}
        opacity={opacity}
        transform={transform}
        {...common}
      />
    ) : (
      <ellipse
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        strokeWidth={width}
        opacity={opacity}
        transform={transform}
        {...common}
      />
    );

  return (
    <>
      {outline(stroke, 1)}
      {outline(stroke * 0.6, 0.55, `rotate(${b.level === 1 ? 5 : -6} ${String(cx)} ${String(cy)})`)}
      {b.text.map((d) => (
        <path
          key={d}
          d={d}
          stroke={b.color}
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.85"
        />
      ))}
    </>
  );
}

export function GlobalLoaderMindMap() {
  const visible = useIsVeiled();
  if (!visible) return null;

  return (
    <GlobalLoaderOverlay>
      <svg
        viewBox="0 0 360 360"
        fill="none"
        aria-hidden
        className="global-loader-mindmap size-96 motion-reduce:[&_*]:animate-none"
      >
        <defs>
          <linearGradient
            id="mm-aurora"
            x1="132"
            y1="214"
            x2="228"
            y2="146"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="var(--color-brand)" />
            <stop offset="100%" stopColor="var(--color-warning)" />
          </linearGradient>
          {/* Tremblement « dessiné à la main » : bruit + déplacement léger. */}
          <filter id="mm-sketch" x="-5%" y="-5%" width="110%" height="110%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.045"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="2.4"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>

        <g filter="url(#mm-sketch)">
          {/* Mind map : trait + bulle par groupe (allumage en vague branche par branche) */}
          {BUBBLES.map((b) => (
            <g key={`${String(b.bubble[0])}-${String(b.bubble[1])}`} className="mm-branch">
              <line
                x1={b.line[0]}
                y1={b.line[1]}
                x2={b.line[2]}
                y2={b.line[3]}
                stroke={b.color}
                strokeWidth={b.level === 1 ? 1.8 : 1.4}
                strokeLinecap="round"
              />
              <BubbleShape b={b} />
            </g>
          ))}

          {/* Nœud central — boussole, cercle repassé deux fois comme la référence */}
          <circle
            cx="180"
            cy="180"
            r="48"
            fill="var(--color-surface)"
            stroke="url(#mm-aurora)"
            strokeWidth="3"
          />
          <circle
            cx="180"
            cy="180"
            r="48"
            stroke="url(#mm-aurora)"
            strokeWidth="1.6"
            opacity="0.6"
            transform="rotate(7 180 180) translate(2 -1)"
          />
          <circle
            cx="180"
            cy="180"
            r="48"
            stroke="url(#mm-aurora)"
            strokeWidth="1.2"
            opacity="0.4"
            transform="rotate(-9 180 180) translate(-2 1)"
          />
        </g>

        {/* Graduations cardinales */}
        {[0, 90, 180, 270].map((deg) => (
          <line
            key={deg}
            x1="180"
            y1="140"
            x2="180"
            y2="146"
            stroke="var(--color-fg-muted)"
            strokeWidth="1.5"
            strokeLinecap="round"
            transform={`rotate(${String(deg)} 180 180)`}
          />
        ))}
        {/* Aiguille (nord violet, sud ambre) */}
        <g className="mm-needle">
          <path d="M180 148 L172 180 L188 180 Z" fill="var(--color-brand)" />
          <path d="M180 212 L172 180 L188 180 Z" fill="var(--color-warning)" opacity="0.8" />
        </g>
        <circle cx="180" cy="180" r="5" fill="var(--color-warning)" />
      </svg>
    </GlobalLoaderOverlay>
  );
}
